import { useFormik } from "formik";
import React from "react";
import * as Yup from "yup";
import { useGraphQLAuth } from "../../hooks/useGraphQLAuth";
import { useToast } from "../../hooks/useToast";
import { Button } from "../Button";
import { InputFormField } from "../InputFormField";
import { Questionnaire } from "../QuestionsList";
import { TextFormField } from "../TextFormField";
import { Title } from "../Title";
import { FormActions, FormContainer, FormContent } from "./styles";

interface QuestionnaireUpdateFormProps {
  questionnaire: Questionnaire;
  onSuccess?: (updatedQuestionnaire: Questionnaire) => void;
  onCancel?: () => void;
}

interface FormValues {
  name: string;
  description: string;
  passingScore: number;
}

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Nome é obrigatório")
    .min(3, "Nome deve ter pelo menos 3 caracteres"),
  description: Yup.string().optional(),
  passingScore: Yup.number()
    .required("Pontuação mínima é obrigatória")
    .min(0, "Pontuação mínima deve ser maior ou igual a 0")
    .max(100, "Pontuação mínima deve ser menor ou igual a 100")
    .integer("Pontuação mínima deve ser um número inteiro"),
});

export const QuestionnaireUpdateForm: React.FC<
  QuestionnaireUpdateFormProps
> = ({ questionnaire, onSuccess, onCancel }) => {
  const { connectGraphQLApi } = useGraphQLAuth();
  const { setToast } = useToast();

  const initialValues: FormValues = {
    name: questionnaire.name || "",
    description: questionnaire.description || "",
    passingScore: questionnaire.passingScore || 0,
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      // Check if any values have actually changed
      const hasChanges =
        values.name.trim() !== (questionnaire.name || "") ||
        values.description?.trim() !== (questionnaire.description || "") ||
        values.passingScore !== (questionnaire.passingScore || 0);

      if (!hasChanges) {
        setToast({
          type: "warning",
          message: "Nenhuma alteração foi feita",
          isActive: true,
          timeToRemoveToast: 3000,
        });
        return;
      }
      try {
        await connectGraphQLApi({
          query: `
            mutation BackofficeUpdateQuestionnaire($input: UpdateQuestionnaireInput!) {
              backofficeUpdateQuestionnaire(input: $input) {
                questionnaireId
                name
                description
                passingScore
                status
                updateDate
              }
            }
          `,
          variables: {
            input: {
              questionnaireId: questionnaire.questionnaireId,
              name: values.name.trim(),
              description: values.description?.trim() || null,
              passingScore: values.passingScore,
            },
          },
          errorMessage: "Falha ao atualizar questionário",
          useNewApi: true,
        });

        setToast({
          type: "success",
          message: "Questionário atualizado com sucesso!",
          isActive: true,
          timeToRemoveToast: 3000,
        });

        if (onSuccess) {
          // Create updated questionnaire object with new values
          const updatedQuestionnaire: Questionnaire = {
            ...questionnaire,
            name: values.name.trim(),
            description: values.description?.trim() || undefined,
            passingScore: values.passingScore,
            updateDate: new Date().toISOString(),
          };
          onSuccess(updatedQuestionnaire);
        }
      } catch (error: any) {
        console.error("Error updating questionnaire:", error);

        // Handle specific GraphQL errors
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          const graphQLError = error.graphQLErrors[0];
          const errorMessage =
            graphQLError.message || "Erro ao atualizar questionário";

          setToast({
            type: "error",
            message: errorMessage,
            isActive: true,
            timeToRemoveToast: 5000,
          });
        } else {
          setToast({
            type: "error",
            message: "Erro inesperado ao atualizar questionário",
            isActive: true,
            timeToRemoveToast: 5000,
          });
        }
      }
    },
  });

  return (
    <FormContainer>
      <FormContent>
        <Title
          elementHTML="h3"
          text="Editar Questionário"
          className="subtitle"
        />

        <form onSubmit={(e) => e.preventDefault()}>
          <InputFormField
            label="Nome do Questionário"
            name="name"
            value={values.name}
            onChange={handleChange}
            error={touched.name && !!errors.name}
            mensageError={errors.name}
            marginBotton="20"
            block
          />

          <TextFormField
            label="Descrição"
            name="description"
            value={values.description}
            onChange={handleChange}
            error={touched.description && !!errors.description}
            mensageError={errors.description}
            marginBotton="20"
            block
            rows={3}
          />

          <InputFormField
            label="Pontuação mínima para obter o certificado"
            name="passingScore"
            type="number"
            value={values.passingScore.toString()}
            onChange={(e) => {
              const value = parseInt(e.target.value) || 0;
              setFieldValue("passingScore", value);
            }}
            error={touched.passingScore && !!errors.passingScore}
            mensageError={errors.passingScore}
            marginBotton="30"
            block
          />

          <FormActions>
            <Button
              label="Cancelar"
              typeBtn="outline"
              onClick={onCancel}
              disable={isSubmitting}
            />
            <Button
              label="Salvar"
              typeBtn="blue"
              type="button"
              load={isSubmitting}
              disable={isSubmitting}
              onClick={() => handleSubmit()}
            />
          </FormActions>
        </form>
      </FormContent>
    </FormContainer>
  );
};
